import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { sizeCurveApi, sizeSchemaApi } from '../../services/api';

export default function SizeCurvesDashboard() {
  const [curves, setCurves] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ schema_key: '', curve_name: '', ordered_sizes: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [cResp, sResp] = await Promise.all([sizeCurveApi.list(), sizeSchemaApi.list()]);
      setCurves(cResp.curves || []);
      setSchemas((sResp.schemas || []).filter(s => s.active && s.schema_key !== 'unknown'));
    } catch (e) {
      setError(e.message || 'Failed to load curves');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData({ schema_key: '', curve_name: '', ordered_sizes: '', notes: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (c) => {
    setEditing(c);
    setFormData({
      schema_key: c.schema_key,
      curve_name: c.curve_name,
      ordered_sizes: (c.ordered_sizes || []).join(', '),
      notes: c.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const ordered_sizes = formData.ordered_sizes
        .split(',').map(s => s.trim()).filter(Boolean);
      if (ordered_sizes.length === 0) {
        setError('ordered_sizes must not be empty');
        return;
      }
      if (editing) {
        await sizeCurveApi.update(editing.id, {
          curve_name: formData.curve_name,
          ordered_sizes,
          notes: formData.notes,
        });
      } else {
        await sizeCurveApi.create({
          schema_key: formData.schema_key,
          curve_name: formData.curve_name,
          ordered_sizes,
          notes: formData.notes,
        });
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (c) => {
    try {
      await sizeCurveApi.update(c.id, { active: !c.active });
      await load();
    } catch (e) {
      setError(e.message || 'Failed to toggle');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete curve "${c.curve_name}" for ${c.schema_key}?`)) return;
    try {
      await sizeCurveApi.delete(c.id);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Size Curves</Typography>
          <Typography variant="body2" color="text.secondary">
            Ordered size lists per schema. Used by the API to provide curve_position for
            frontend size injection. Multiple curves per schema allowed (e.g. adult vs child).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Curve</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Schema</strong></TableCell>
              <TableCell><strong>Curve Name</strong></TableCell>
              <TableCell><strong>Ordered Sizes</strong></TableCell>
              <TableCell><strong>Notes</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {curves.map(c => (
              <TableRow key={c.id} sx={{ opacity: c.active ? 1 : 0.5 }}>
                <TableCell><Chip label={c.schema_key} size="small" color="primary" /></TableCell>
                <TableCell>{c.curve_name}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(c.ordered_sizes || []).map((s, i) => (
                      <Chip key={`${c.id}-${i}-${s}`} label={s} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{c.notes || '-'}</Typography></TableCell>
                <TableCell align="center"><Switch checked={c.active} onChange={() => handleToggleActive(c)} size="small" /></TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEdit(c)}>Edit</Button>
                  <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => handleDelete(c)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {curves.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No curves yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Curve' : 'Add Curve'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" disabled={!!editing}>
            <InputLabel>Schema</InputLabel>
            <Select value={formData.schema_key} label="Schema" onChange={(e) => setFormData({ ...formData, schema_key: e.target.value })}>
              {schemas.map(s => <MenuItem key={s.schema_key} value={s.schema_key}>{s.schema_key} — {s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Curve Name"
            value={formData.curve_name}
            onChange={(e) => setFormData({ ...formData, curve_name: e.target.value })}
            fullWidth margin="normal"
            helperText="e.g. standard, child, women's"
          />
          <TextField
            label="Ordered Sizes (comma-separated, lowest to highest)"
            value={formData.ordered_sizes}
            onChange={(e) => setFormData({ ...formData, ordered_sizes: e.target.value })}
            fullWidth margin="normal" multiline rows={2}
            helperText="Use canonical normalized_size values, e.g. XXS, XS, S, M, L, XL, XXL, 3XL"
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
